import { createSelector } from '@reduxjs/toolkit';

import {
    liveCheckTripAction,
    liveCheckSelectors,
} from 'wanderu-ducks-live-check';
import { tripSelectors } from 'wanderu-ducks-trips';
import { createAppSelector } from 'wanderu-ducks-utils';
import { selectCurrency } from 'wanderu-i18n';

import NAMESPACE from './types';
import SLICE_NAMESPACE from '../../types';

const EMPTY_ITINERARIES = Object.freeze([]);

function selectData(state) {
    return state[SLICE_NAMESPACE][NAMESPACE];
}

function selectTravelerInfo(state) {
    return selectData(state);
}

const selectTripIdsWithMismatchedCurrency = createAppSelector(
    [
        selectCurrency,
        (state, tripId) => tripSelectors.selectTripSet(state, tripId),
    ],
    (selectedCurrency, trips) => {
        return trips.reduce((arr, trip) => {
            if (!trip.pricing?.length) {
                return arr;
            }
            if (!Object.prototype.hasOwnProperty.call(trip.pricing[0], selectedCurrency)) {
                arr.push(trip.tripId);
            }
            return arr;
        }, []);
    },
);

// NOTE: Similar pattern exists in wanderu-component-itinerary/ducks/appstate/selectors.js.
// Both map legIds → selectTripItinerary. If either is modified, consider consolidating into wanderu-ducks-trips.
const selectItinerary = createAppSelector(
    [
        (state, tripId) => {
            const legIds = tripSelectors.selectLegIds(state, tripId);
            if (!tripId || !legIds) return EMPTY_ITINERARIES;
            return legIds.map((id) => tripSelectors.selectTripItinerary(state, id));
        },
    ],
    (itineraries) => itineraries,
);

function selectPrices(state, tripIds, seats, wheelchairs, showTotalPrice = false) {
    const ids = tripIds.filter(Boolean);

    return ids.reduce((idSet, id) => {
        const tripPrices = tripSelectors
            .selectTripPrices(state, id, seats, wheelchairs, showTotalPrice);
        const pricing = tripPrices?.pricing || [];
        return [...idSet, ...pricing];
    }, []);
}

function selectAvailableFares(state, tripId, seats, wheelchairs) {
    return tripSelectors.selectAvailableFares(state, tripId, seats, wheelchairs);
}

const selectAvailableFareNames = createAppSelector(
    [tripSelectors.selectAvailableFares],
    (fares = {}) => {
        const tripIdsList = Object.keys(fares);
        const tripFareMap = {};

        for (const tripId of tripIdsList) {
            const hasFares = Boolean(fares[tripId].length);
            const notInMap = !tripFareMap[tripId];

            if (hasFares && notInMap) {
                tripFareMap[tripId] = fares[tripId].map(fare => fare.description.title);
            }
        }

        return tripFareMap;
    },
);

function selectLcParams(state, tripId, opts) {
    const {
        seats,
        wheelchairs,
    } = opts || selectTravelerInfo(state);
    const {
        selectedFare,
    } = tripSelectors.selectTripData(state, tripId) || {};

    return {
        seats,
        selectedFare,
        tripId,
        wheelchairs,
    };
}

function selectNativeBookButtonSupport(state, tripIds, opts) {
    const lcsCompleted = selectLiveChecksCompleted(state, tripIds, opts);
    if (!lcsCompleted) {
        return undefined; // need to wait for all LCs to complete before knowing for sure
    }

    return tripIds.length > 0 && tripIds.every((tripId) => {
        const lcParams = selectLcParams(state, tripId, opts);
        return liveCheckSelectors.selectTripNativeBooking(state, lcParams);
    });
}

function selectSingleDeeplinkBookButtonSupport(state, tripIds, opts) {
    const lcsCompleted = selectLiveChecksCompleted(state, tripIds, opts);
    if (!lcsCompleted) {
        return undefined; // need to wait for all LC calls to complete before knowing for sure
    }

    if (tripIds.length === 0) {
        return false;
    }

    /* GBREZ trips include a `roundTripEnabled` field which is a list of deeplink trip ids
     * that constitute a round-trip.  We only want to show a single book button for them
     * it's safe to assume that if one trip has this field then they all have this field.
     * we don't (currently) route GBREZ with other carriers */

    const lcParams = selectLcParams(state, tripIds[0], opts);
    let { roundTripBundle = [] } = liveCheckSelectors.selectTripCheckoutData(
        state,
        lcParams,
    ) || {};

    return tripIds.every((tripId) => roundTripBundle.includes(tripId));
}

const selectSingleBookButtonSupport = createSelector(
    selectNativeBookButtonSupport,
    selectSingleDeeplinkBookButtonSupport,
    (native, singleDeepLink) => (native === undefined || singleDeepLink === undefined)
        ? undefined
        : !!native || !!singleDeepLink,
);

function selectCheckoutData(state, tripIds = [], opts = {}) {
    if (!tripIds.length) {
        return undefined;
    }

    return tripIds.filter(Boolean)
        .reduce((allIds, tripId) => [
            ...allIds,
            ...tripSelectors.selectLegIds(state, tripId),
        ], [])
        .reduce((checkoutData, tripId) => {
            const lcParams = selectLcParams(state, tripId, opts);
            return {
                ...checkoutData,
                [tripId]: liveCheckSelectors.selectTripCheckoutData(state, lcParams),
            };
        }, {});
}

function selectCarrierDataHelper(state, tripIds) {
    if (!tripIds || !tripIds.length) {
        return undefined;
    }

    const ids = tripIds.filter(Boolean).reduce((arr, tripId) => ([
        ...arr,
        ...tripSelectors.selectLegIds(state, tripId),
    ]), []);

    return ids.reduce((arr, id) => [
        ...arr,
        tripSelectors.selectTripCarrierData(state, id),
    ], []);
}

// TODO: Identity result function — the createSelector wrapper exists solely for argsMemoize caching.
// Consider inlining selectCarrierDataHelper or giving it a meaningful transform.
const selectCarrierData = createAppSelector(
    [(state, tripIds) => selectCarrierDataHelper(state, tripIds)],
    (carriers) => carriers,
);

const selectServiceAttribution = createAppSelector(
    [(state, tripIds) => selectCarrierDataHelper(state, tripIds)],
    (carriers) => {
        if (!carriers || !carriers.length) {
            return undefined;
        }

        const services = carriers.reduce((serviceNameList, carrier) => {
            if (carrier.supplementalInfo && carrier.supplementalInfo.serviceAttribution) {
                serviceNameList.push(carrier.supplementalInfo.serviceAttribution.name);
            }
            return serviceNameList;
        }, []);

        return services[0] || '';
    },
);

function selectTripPrice(state, tripId, seats, wheelchairs, showTotalPrice = false) {
    /* the wanderu-ducks-live-check selector returns pricing as an array
     * as a convenience for search results which display routed trips,
     * but we only ever select non-routed trips here */
    const { fees, pricing } = tripSelectors.selectTripPrices(
        state,
        tripId,
        seats,
        wheelchairs,
        showTotalPrice,
    ) || {};
    return { fees, pricing };
}

function selectLiveChecksInProgress(state, tripIds, opts) {
    return tripIds.some((tripId) => {
        const lcParams = selectLcParams(state, tripId, opts);
        return liveCheckTripAction.selectWorking(state, lcParams);
    });
}

function selectLiveChecksCompleted(state, tripIds, opts) {
    return tripIds.every((tripId) => {
        const lcParams = selectLcParams(state, tripId, opts);
        const hasErrored = !!liveCheckTripAction.selectError(state, lcParams);
        const hasSucceeded = liveCheckTripAction.selectSuccess(state, lcParams);
        return hasErrored || hasSucceeded;
    });
}

function selectLiveChecksSuccess(state, tripIds, opts) {
    return tripIds.every((tripId) => {
        const lcParams = selectLcParams(state, tripId, opts);
        return liveCheckTripAction.selectSuccess(state, lcParams);
    });
}

function selectLiveCheckEarliestExpiresAt(state, tripIds, opts) {
    return tripIds.reduce((earliestExpiresAt, tripId) => {
        const lcParams = selectLcParams(state, tripId, opts);
        const expiresAt = liveCheckTripAction.selectExpiresAt(state, lcParams) || 0;

        if (typeof earliestExpiresAt === 'undefined' || expiresAt < earliestExpiresAt) {
            return expiresAt;
        }

        return earliestExpiresAt;
    }, undefined);
}

function selectLiveChecksHaveExpired(state, tripIds, opts) {
    return tripIds.some((tripId) => {
        const lcParams = selectLcParams(state, tripId, opts);
        return liveCheckTripAction.selectExpired(state, lcParams);
    });
}

function buildLiveCheckInputs(state, tripIds, opts) {
    const lcParamsArr = tripIds.map((tripId) => selectLcParams(state, tripId, opts));
    const { originalPricing, originalSeats } = tripIds.reduce((acc, tripId) => {
        const trip = tripSelectors.selectTripData(state, tripId) || {};
        return {
            originalPricing: { ...acc.originalPricing, [tripId]: trip.pricing },
            // The seat count the original (search) price represents. The live-
            // check detection normalizes the original price by this — NOT by the
            // requested passenger count — so a per-person single-pax-cache price
            // (trip.seats === '1') isn't double-discounted into a phantom change.
            originalSeats: { ...acc.originalSeats, [tripId]: trip.seats },
        };
    }, { originalPricing: {}, originalSeats: {} });
    return { lcParamsArr, originalPricing, originalSeats };
}

function selectLiveCheckSummaryStatus(state, tripIds, opts) {
    const { lcParamsArr, originalPricing, originalSeats } = buildLiveCheckInputs(
        state,
        tripIds,
        opts,
    );
    return liveCheckSelectors.selectLiveCheckSummaryStatus(
        state,
        lcParamsArr,
        originalPricing,
        originalSeats,
    );
}

function selectLiveCheckData(state, tripIds, opts) {
    const { lcParamsArr, originalPricing, originalSeats } = buildLiveCheckInputs(
        state,
        tripIds,
        opts,
    );
    return liveCheckSelectors.selectLiveCheckData(
        state,
        lcParamsArr,
        originalPricing,
        originalSeats,
    );
}

function selectSeatInformation(state, tripIds) {
    const tripList = tripIds.reduce((arr, tripId) => {
        const trip = tripSelectors.selectTrip(state, tripId);
        const carrier = tripSelectors.selectTripCarrierData(state, tripId);
        const isAdaCompliant = !!(trip.accessibility
            && trip.accessibility.maxWheelchairSeats);

        arr.push({
            carrierName: carrier.name,
            isAdaCompliant,
            maxSeats: carrier.maxSeats,
            maxWheelchairs: isAdaCompliant
                ? trip.accessibility.maxWheelchairSeats
                : 0,
            tripId: trip.tripId,
        });

        return arr;
    }, []);

    const maxSeats = Math.min(...tripList.map((trip) => trip.maxSeats));
    const maxWheelchairs = Math.min(...tripList.map((trip) => trip.maxWheelchairs));

    const nonAdaCarriers = tripList.reduce((arr, trip) => {
        if (!trip.isAdaCompliant) {
            arr.add(trip.carrierName);
        }
        return arr;
    }, new Set());

    return {
        maxSeats,
        maxWheelchairs,
        nonAdaCarriers: [...nonAdaCarriers],
    };
}

export {
    selectAvailableFareNames,
    selectAvailableFares,
    selectCarrierData,
    selectCheckoutData,
    selectItinerary,
    selectLiveCheckData,
    selectLiveCheckEarliestExpiresAt,
    selectLiveCheckSummaryStatus,
    selectLiveChecksCompleted,
    selectLiveChecksHaveExpired,
    selectLiveChecksInProgress,
    selectLiveChecksSuccess,
    selectPrices,
    selectSeatInformation,
    selectServiceAttribution,
    selectSingleBookButtonSupport,
    selectSingleDeeplinkBookButtonSupport,
    selectTravelerInfo,
    selectTripIdsWithMismatchedCurrency,
    selectTripPrice,
};
